/**
 * BIES HOUSE - Modern Cart Drawer
 * Handles all cart drawer functionality
 */

// Debug utility — only logs when window.BIES_DEBUG is truthy
function biesDebug(...args) {
  if (window.BIES_DEBUG) console.log(...args);
}

// Prevent double initialization
if (window.biesCartDrawerInitialized) {
  biesDebug('[BIES Cart Drawer] Already initialized, skipping duplicate');
} else {
  window.biesCartDrawerInitialized = true;
  biesDebug('[BIES Cart Drawer] Script loaded');
}

class BiesCartDrawer {
  // Nombre de la section para la Section Rendering API — usado al pedir
  // sections= en add.js Y al extraer la respuesta. Un solo sitio: un rename
  // de la section se cambia aquí (y en el archivo .liquid).
  static SECTION_ID = 'bies-modern-cart-drawer';
  // Section del contador del header (Dawn: sections/cart-icon-bubble.liquid).
  // Se pide junto al drawer en CADA mutación para que la burbuja del header
  // refleje el carrito aunque el user cierre el drawer (antes nunca se tocaba
  // y seguía en 0 tras añadir — fix 8-sep).
  static BUBBLE_SECTION_ID = 'cart-icon-bubble';
  static SECTIONS = [BiesCartDrawer.SECTION_ID, BiesCartDrawer.BUBBLE_SECTION_ID];

  constructor() {
    this.drawer = null;
    this.overlay = null;
    this.REWARDS_TIERS = [
      { threshold: 4000, giftName: 'Bebida Tropical', shortName: 'Tropical', giftVariantId: 53867542511963, realValue: 1250 },
      { threshold: 5000, giftName: 'Stevia BIES', shortName: 'BIES', giftVariantId: 52456161771867, realValue: 1450 },
      { threshold: 7000, giftName: 'Batido de Proteínas', shortName: 'Batido', giftVariantId: 52456162066779, realValue: 1950 }
    ];
    this.REWARDS_MAX = 7000;
    // 52456154431835 = REGALO Granola, retirado del tier 40€ el 2026-08-23 (swap a Bebida Tropical por falta de stock de granola).
    // Se MANTIENE en el Set ~4 semanas para que syncGiftProducts limpie los carritos en vuelo que aún lo tengan (evita línea fantasma 0€ + doble regalo). Retirar tras drenar.
    this.GIFT_VARIANT_IDS = new Set([52456154431835, 53867542511963, 52456161771867, 52456162066779]);

    // ── Concurrencia (rediseño 13-jul, mega-code-review) ──────────────────
    // Cola serie: UNA operación de carrito a la vez. Mata el overshoot del
    // doble-click (dos /cart/change.js concurrentes) y las respuestas fuera
    // de orden que pintaban estados viejos ("se queda estancada").
    this._opQueue = Promise.resolve();
    this._seq = 0;            // epoch monotónico de estados de carrito
    this._renderedSeq = -1;   // último seq efectivamente pintado
    this._loadingCount = 0;   // refcount de setLoading (no toggle suelto)
    this._mutating = false;   // guarda contra reentradas por 'cart:updated' propio
    this._userOps = 0;        // nº de ops iniciadas por el usuario (add/+/-/remove):
                              // un refresh de fondo que arrancó antes NO pinta si
                              // el usuario actuó mientras tanto (fix 8-sep: el
                              // refresh inicial pisaba el skeleton con "vacío")

    // ── Caches de recomendaciones (14-jul, seamless) ──────────────────────
    // Sin esto, cada add/remove relanzaba la cascada completa: 1 fetch de
    // /products/{handle}.js POR item + 1 de /recommendations POR item + el
    // fallback — con el bloque en "Cargando..." y salto de layout. Los datos
    // son estables durante la vida de la página → cache en memoria.
    this._productTypeCache = new Map();   // handle → tipo inferido
    this._recsCache = new Map();          // product_id → recs Shopify API
    this._allProductsCache = null;        // /products.json (pool de fallback)
    this._recoRunId = 0;                  // carrera: solo pinta el run más nuevo
    this._lastRenderedRecoIds = '';       // IDs pintados (skip swap idéntico)
    this._lastRecoKey = null;             // set de productos del último run de recos
                                          // (null, NO '': un carrito vacío produce
                                          // recoKey '' y debe correr la primera vez)

    this.init();
  }

  // Encola una operación de carrito. Las ops corren en serie: la siguiente
  // no empieza hasta que la anterior (con su render) termina.
  _serial(taskFn) {
    const run = () => Promise.resolve()
      .then(() => { this._mutating = true; })
      .then(taskFn)
      .catch((e) => console.error('[BIES Cart Drawer] op error:', e))
      .finally(() => { this._mutating = false; });
    this._opQueue = this._opQueue.then(run, run);
    return this._opQueue;
  }

  // Fetch autoritativo del carrito con número de secuencia (para descartar
  // renders fuera de orden en renderCart).
  async _authoritativeCart() {
    const cart = await (await fetch('/cart.js')).json();
    cart.__seq = ++this._seq;
    return cart;
  }

  // Carrito autoritativo de una respuesta de add.js: JSON embebido en la
  // section (script#bies-cart-json-data) si vino, fetch clásico a /cart.js si
  // no. Único punto de resolución para los paths de add (form interceptor +
  // addRecommendedProduct). Desde T03 (opción D) el HTML de esa MISMA section
  // SÍ se usa como render (swapFromSections) — antes se descartaba; el 13-jul
  // rompió por swapear un template INCOMPLETO, no por server-render (ahora el
  // partial Liquid es completo: data-item-key, precios, regalos, bundles).
  async _cartFromAddResponse(responseData) {
    return this._cartFromSectionsResponse(responseData) || await this._authoritativeCart();
  }

  // Extrae de una respuesta con sections= los dos HTML que renderizamos:
  // el drawer y la burbuja del header. Único punto de lectura de `sections`.
  _sectionsFromResponse(data) {
    const sections = (data && data.sections) || {};
    return {
      html: sections[BiesCartDrawer.SECTION_ID] || null,
      bubbleHtml: sections[BiesCartDrawer.BUBBLE_SECTION_ID] || null
    };
  }

  // Sincroniza #cart-icon-bubble del header. Con el HTML de la section lo
  // swapea entero (icono vacío/lleno + contador); sin él, ajusta solo el
  // contador a partir de cart.item_count (fallback determinista).
  _syncHeaderBubble(cart, bubbleHtml) {
    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    if (bubbleHtml) {
      try {
        const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const section = doc.querySelector('.shopify-section') || doc.body;
        link.innerHTML = section.innerHTML;
        return;
      } catch (e) {
        biesDebug('[BIES Cart Drawer] bubble swap parse failed — fallback a item_count', e);
      }
    }
    const count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
    if (count === null) return;
    let bubble = link.querySelector('.cart-count-bubble');
    if (count === 0) { if (bubble) bubble.remove(); return; }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      link.appendChild(bubble);
    }
    bubble.innerHTML = count < 100 ? `<span aria-hidden="true">${count}</span>` : '';
  }

  _cartFromSectionsResponse(responseData) {
    try {
      const html = responseData && responseData.sections && responseData.sections[BiesCartDrawer.SECTION_ID];
      if (!html) return null;
      // DOMParser: no ejecuta scripts NI dispara carga de <img> (un div
      // desconectado con innerHTML sí precarga imágenes).
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const scriptEl = doc.getElementById('bies-cart-json-data');
      if (!scriptEl) return null;
      const cart = JSON.parse(scriptEl.textContent);
      if (!cart || typeof cart.item_count !== 'number' || !Array.isArray(cart.items)) return null;
      cart.__seq = ++this._seq;
      return cart;
    } catch (e) {
      biesDebug('[BIES Cart Drawer] sections JSON extract failed — fallback a /cart.js', e);
      return null;
    }
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    biesDebug('[BIES Cart Drawer] Setup starting...');
    this.drawer = document.getElementById('bies-cart-drawer');
    this.overlay = document.getElementById('bies-cart-drawer-overlay');

    if (!this.drawer) {
      console.error('[BIES Cart Drawer] CRITICAL: Drawer element #bies-cart-drawer not found!');
      return;
    }

    biesDebug('[BIES Cart Drawer] Drawer element found, binding events...');
    this.bindEvents();

    // Defer cart pipeline to idle time — drawer is hidden, no need to block page load
    this._cartReady = false;
    this._lastCartUpdate = 0;
    const runUpdate = () => {
      // Enrutado por la cola serie (M3, mega-code-review): así el refresh de
      // open()/setup() no se solapa con una mutación encolada (last-writer-wins
      // sin control de orden tras borrar el epoch _seq muerto).
      // fromPage (8-sep): el Liquid YA renderizó el drawer con el carrito
      // actual y embebió su JSON — reconciliamos desde ahí, sin /cart.js ni
      // /cart/update.js. Antes eran 2 round-trips que en móvil retenían el
      // primer "añadir" ~3s y luego pisaban su skeleton con el estado viejo.
      this._serial(() => this.updateCart({ fromPage: true, background: true })).then(() => { this._cartReady = true; });
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(runUpdate, { timeout: 2000 });
    } else {
      setTimeout(runUpdate, 100);
    }

    biesDebug('[BIES Cart Drawer] Setup complete');
  }

  bindEvents() {
    biesDebug('[BIES Cart Drawer] bindEvents() called');

    // Close buttons
    const closeBtns = this.drawer.querySelectorAll('[data-cart-drawer-close]');
    closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Store reference to this for event handlers
    const self = this;

    // Unified click handler for all cart drawer interactions
    biesDebug('[BIES Cart Drawer] Adding click handler to drawer');
    this.drawer.addEventListener('click', function(e) {
      biesDebug('[BIES Cart Drawer] Click detected on:', e.target.tagName, e.target.className);
      // Captura de scrollTop ANTES de cualquier mutación (lo más temprano): el
      // swap del server (opción D) colapsa la altura de la lista un instante y
      // el navegador clampa scrollTop a 0. Guardamos aquí; swapFromSections lo
      // restaura tras el swap para que mutar a media lista no salte al inicio.
      if (e.target.closest('[data-qty-action], [data-remove-item], [data-add-recommended]')) {
        const _body = self.drawer.querySelector('.bies-cart-drawer__body');
        if (_body) self._savedScroll = _body.scrollTop;
        self._userOps++;  // invalida el swap de cualquier refresh de fondo en vuelo
      }
      // Handle quantity buttons
      const qtyBtn = e.target.closest('[data-qty-action]');
      if (qtyBtn) {
        e.preventDefault();
        e.stopPropagation();
        const action = qtyBtn.getAttribute('data-qty-action');
        const lineIndex = qtyBtn.getAttribute('data-line-index');
        biesDebug('[BIES Cart Drawer] Qty button clicked:', action, 'line:', lineIndex);
        self._serial(() => self.updateQuantity(lineIndex, action));
        return;
      }

      // Handle remove item buttons
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const lineIndex = removeBtn.getAttribute('data-line-index');
        // Capturamos también el variant-id: en un doble-click el 2º click se
        // encola con el lineIndex viejo antes del re-render, y tras borrar el
        // 1º la lista se re-numera → borrar por índice mataría el item de al
        // lado. removeItem re-resuelve el índice por variant-id en ejecución.
        const itemEl = removeBtn.closest('.bies-cart-item');
        const variantId = itemEl ? itemEl.getAttribute('data-variant-id') : null;
        biesDebug('[BIES Cart Drawer] Remove button clicked for line:', lineIndex, 'variant:', variantId);
        self._serial(() => self.removeItem(lineIndex, variantId));
        return;
      }

      // Handle add recommended product buttons
      const addBtn = e.target.closest('[data-add-recommended]');
      if (addBtn) {
        e.preventDefault();
        e.stopPropagation();
        const variantId = addBtn.getAttribute('data-variant-id');
        biesDebug('[BIES Cart Drawer] Add recommended clicked:', variantId);
        self._serial(() => self.addRecommendedProduct(variantId, addBtn));
        return;
      }
    });

    // Listen for cart updates from OTHER sources (p.ej. free-shipping bar).
    // Se encola, y se ignora si viene de una mutación propia en curso
    // (nuestro processCart ya reconcilia) — evita la cascada concurrente.
    document.addEventListener('cart:updated', () => {
      if (this._mutating) return;
      this._serial(() => this.updateCart({ background: true }));
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  open(options = {}) {
    if (!this.drawer) return;

    this._animateItemsOnce = true; // los items animan su entrada solo al abrir (fix glitch 13-jul)
    this.drawer.classList.add('is-open');
    if (this.overlay) {
      this.overlay.classList.add('is-visible');
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Lazy-render payment icons (~40KB of inline SVG) on first open only.
    // Defensive: never let this break drawer opening if markup is missing.
    this._renderPaymentIcons();

    // Update cart data when opening (skip if caller already did it, or if recently updated)
    if (!options.skipUpdate) {
      const cacheAge = Date.now() - (this._lastCartUpdate || 0);
      if (!this._cartReady || cacheAge > 30000) {
        // Enrutado por la cola serie (M3, mega-code-review): así el refresh de
        // open()/setup() no se solapa con una mutación encolada (last-writer-wins
        // sin control de orden tras borrar el epoch _seq muerto).
        this._serial(() => this.updateCart({ background: true })).then(() => { this._cartReady = true; });
      }
    }

    // Analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cart_drawer_opened'
      });
    }
  }

  /**
   * Clones the payment-icon SVGs from their <template> into the drawer
   * the first time it opens. Idempotent (no-ops if already rendered).
   * Defensive: swallows errors so a markup change can never block the
   * drawer from opening.
   */
  _renderPaymentIcons() {
    if (this._paymentIconsRendered) return;
    try {
      const target = document.getElementById('bies-cart-drawer-payment-icons');
      const tpl = document.getElementById('bies-cart-drawer-payment-icons-template');
      if (!target || !tpl || !tpl.content) return;
      target.appendChild(tpl.content.cloneNode(true));
      this._paymentIconsRendered = true;
    } catch (error) {
      biesDebug('[BIES Cart Drawer] Payment icons lazy-render failed (non-fatal):', error);
    }
  }

  close() {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
    }

    // Restore body scroll
    document.body.style.overflow = '';

    // Analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cart_drawer_closed'
      });
    }
  }

  isOpen() {
    return this.drawer && this.drawer.classList.contains('is-open');
  }

  // Carrito serializado por el Liquid en la propia página (script#bies-cart-json-data
  // dentro de la section): en el load es tan autoritativo como /cart.js y cuesta 0 red.
  _cartFromPageJson() {
    try {
      const el = document.getElementById('bies-cart-json-data');
      if (!el) return null;
      const cart = JSON.parse(el.textContent);
      if (!cart || typeof cart.item_count !== 'number' || !Array.isArray(cart.items)) return null;
      cart.__seq = ++this._seq;
      return cart;
    } catch (e) {
      return null;
    }
  }

  async updateCart({ fromPage = false, background = false } = {}) {
    try {
      let cart = fromPage ? this._cartFromPageJson() : null;
      if (!cart) {
        const response = await fetch('/cart.js');
        cart = await response.json();
      }
      // fromPage: el DOM ya ES el render del server para este carrito → solo
      // reconciliamos (bundles/regalos) y pintamos si algo cambió.
      await this.processCart(cart, { background, skipSwapIfUnchanged: fromPage });
      this._lastCartUpdate = Date.now();
    } catch (error) {
      console.error('[BIES Cart Drawer] Error updating cart:', error);
      this._normalizing = false;
      this._syncingGifts = false;
    }
  }

  /**
   * Process cart data and render. Accepts cart object directly (avoids re-fetching /cart.js).
   * @param {Object} cart - Full cart object (from /cart.js or /cart/change.js response)
   * @param {Object} options
   * @param {boolean} options.skipNormalization - Skip bundle normalization (for simple qty changes on non-bundle items)
   */
  /**
   * Pide al server (Section Rendering API) el drawer YA renderizado para el
   * estado actual del carrito, vía una mutación no-op /cart/update.js con
   * updates:{} + sections=. Devuelve { cart, html } en UN round-trip: el cart
   * autoritativo (top-level de la respuesta) + el HTML de la section. Es la
   * fuente única de verdad del render (opción D).
   */
  async _fetchCartAndSection() {
    const resp = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: {}, sections: BiesCartDrawer.SECTIONS })
    });
    const data = await resp.json();
    data.__seq = ++this._seq;
    const { html, bubbleHtml } = this._sectionsFromResponse(data);
    return { cart: data, html, bubbleHtml };
  }

  /**
   * SWAP del HTML server (opción D): parsea la section renderizada y sustituye
   * SOLO los contenedores que reflejan estado de carrito, preservando el
   * scroll (son hijos de __body, no __body) y las recomendaciones
   * (client-loaded, independientes de la mutación). Sin motor de render
   * cliente. DOMParser NO ejecuta scripts ni precarga <img> de un doc
   * desconectado.
   */
  swapFromSections(html) {
    let doc;
    try {
      doc = new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      biesDebug('[BIES Cart Drawer] swap parse failed', e);
      return;
    }
    const src = id => doc.getElementById(id);
    const dst = id => document.getElementById(id);

    // Restaurar scrollTop capturado en el click handler (a esta altura del
    // flujo el navegador ya lo clampó a 0, así que leerlo aquí no sirve).
    const body = document.querySelector('.bies-cart-drawer__body');
    const savedScroll = (typeof this._savedScroll === 'number') ? this._savedScroll : 0;

    // Items + regalos: innerHTML swap (hijos de __body → no reemplazamos el
    // contenedor de scroll, solo su contenido).
    const newItems = src('bies-cart-items'), curItems = dst('bies-cart-items');
    if (newItems && curItems) { curItems.innerHTML = newItems.innerHTML; this._applyStableOrder(); }

    const newGifts = src('bies-cart-gifts'), curGifts = dst('bies-cart-gifts');
    if (newGifts && curGifts) curGifts.innerHTML = newGifts.innerHTML;

    // Barra de progreso: texto + fill + clase is-complete + milestones (updates
    // puntuales para preservar la transición CSS del fill).
    const newText = src('bies-rewards-text'), curText = dst('bies-rewards-text');
    if (newText && curText) curText.innerHTML = newText.innerHTML;

    const newFill = src('bies-rewards-fill'), curFill = dst('bies-rewards-fill');
    if (newFill && curFill) curFill.style.width = newFill.style.width;

    const newRewards = src('bies-cart-rewards'), curRewards = dst('bies-cart-rewards');
    if (newRewards && curRewards) {
      curRewards.classList.toggle('is-complete', newRewards.classList.contains('is-complete'));
    }
    for (let i = 1; i <= 3; i++) {
      const nm = src(`bies-milestone-${i}`), cm = dst(`bies-milestone-${i}`);
      if (nm && cm) cm.classList.toggle('is-unlocked', nm.classList.contains('is-unlocked'));
    }

    // Subtotal
    const newSub = src('bies-cart-subtotal-value'), curSub = dst('bies-cart-subtotal-value');
    if (newSub && curSub) curSub.textContent = newSub.textContent.trim();

    // Empty-state / body-content / checkout disabled
    const newEmpty = src('bies-cart-empty'), curEmpty = dst('bies-cart-empty');
    const curBody = dst('bies-cart-body-content');
    const isEmpty = newEmpty ? (newEmpty.getAttribute('style') || '').includes('flex') : false;
    if (newEmpty && curEmpty) curEmpty.style.display = isEmpty ? 'flex' : 'none';
    if (curBody) curBody.style.display = isEmpty ? 'none' : 'flex';
    const newCheckout = src('bies-cart-checkout-btn'), curCheckout = dst('bies-cart-checkout-btn');
    if (newCheckout && curCheckout) curCheckout.disabled = newCheckout.hasAttribute('disabled');

    // Restaurar scrollTop: sync + rAF (por si el layout del innerHTML es async
    // o algo lo re-clampa en el mismo tick). Se limpia tras usarlo.
    if (body && savedScroll > 0) {
      body.scrollTop = savedScroll;
      requestAnimationFrame(() => { body.scrollTop = savedScroll; });
    }
    this._savedScroll = 0;
  }

  // ── Orden estable de líneas (fix 8-sep, pedido por Luis) ────────────────
  // Shopify pone lo recién añadido arriba y el cambio de tier (1 → 2x) de los
  // productos con packs quita y re-añade la línea, así que "el caro subía solo"
  // (reproducido: Café 1→2x saltaba por encima de la Granola). Guardamos en
  // localStorage cuándo se vio por primera vez cada línea —clave = bundleCode
  // si el producto tiene tiers, variant_id si no— y ordenamos el DOM por ese
  // instante, más reciente arriba (como Amazon). Solo reordena el DOM del
  // drawer: no toca el carrito de Shopify ni el checkout.
  _stableKey(variantId) {
    const bi = (window.BIES_VARIANT_TO_BUNDLE || {})[String(variantId)];
    return bi ? 'b:' + bi.bundleCode : 'v:' + String(variantId);
  }

  _applyStableOrder() {
    const list = document.getElementById('bies-cart-items');
    if (!list) return;
    const items = [...list.querySelectorAll(':scope > .bies-cart-item[data-variant-id]')];
    if (!items.length) return;
    let seen = {};
    try { seen = JSON.parse(localStorage.getItem('bies_cart_order') || '{}') || {}; } catch (_) { seen = {}; }
    const now = Date.now();
    const keys = items.map(el => this._stableKey(el.getAttribute('data-variant-id')));
    // Líneas nuevas: llegan en orden Shopify (más reciente primero) → timestamps decrecientes para conservarlo entre sí.
    keys.forEach((k, i) => { if (!(k in seen)) seen[k] = now - i; });
    const live = new Set(keys);
    Object.keys(seen).forEach(k => { if (!live.has(k)) delete seen[k]; });   // una línea borrada y re-añadida vuelve arriba
    try { localStorage.setItem('bies_cart_order', JSON.stringify(seen)); } catch (_) {}
    const sorted = items.map((el, i) => ({ el, t: seen[keys[i]], i })).sort((a, b) => (b.t - a.t) || (a.i - b.i));
    if (sorted.every((s, i) => s.el === items[i])) return;
    sorted.forEach(s => list.appendChild(s.el));
  }

  async processCart(cart, { skipNormalization = false, html = null, bubbleHtml = null, background = false, skipSwapIfUnchanged = false } = {}) {
    // Render SERVER-OWNED (opción D): reconcilia normalize + gifts, luego SWAPEA
    // el HTML de la section renderizada por el Liquid (único renderer). Si la
    // mutación original trajo el HTML (sections=) y ni normalize ni gifts
    // cambiaron el estado, se usa ese HTML → 1 round-trip. Si el estado cambió,
    // se re-pide la section reconciliada (≤2 round-trips al cruzar umbral/tier).
    // background (8-sep): refresh no iniciado por el usuario (setup/open/
    // cart:updated). Si el usuario actúa mientras corre, NO pinta: su op, que
    // va detrás en la cola, trae el estado más nuevo. skipSwapIfUnchanged: el
    // DOM ya es el render autoritativo (load de página) → sin fetch ni swap
    // salvo que la reconciliación cambie el carrito.
    const userOpsAtStart = this._userOps;
    let stateChanged = false;
    this.setLoading(true);
    try {
      if (cart.__seq == null) cart.__seq = ++this._seq;
      // sectionHtml empieza siendo el HTML que trajo la mutación (sections=).
      // Se invalida si normalize cambia el estado; se refresca con el HTML del
      // reconcile de gifts si estos cambian; si al final no hay HTML fresco, se
      // re-pide la section una sola vez.
      let sectionHtml = html;

      // 1. Normalizar bundles (dedup / qty>1 → tier correcto).
      if (!skipNormalization) {
        let changed = true, attempts = 5;
        while (changed && attempts-- > 0) {
          changed = await this.normalizeBundleQuantities(cart);
          if (changed) { cart = await this._authoritativeCart(); sectionHtml = null; bubbleHtml = null; stateChanged = true; }
        }
      }

      // 2. Sincronizar regalos escalonados (add/remove según umbral no-regalo).
      //    Si cambian, su respuesta trae el HTML reconciliado → lo usamos.
      const gift = await this.syncGiftProducts(cart);
      if (gift.changed) {
        if (gift.cart) cart = gift.cart;
        sectionHtml = gift.html || null;
        bubbleHtml = gift.bubbleHtml || null;
        stateChanged = true;
      }

      // 2b. Un refresh de fondo no pisa lo que el usuario acaba de pedir.
      if (background && this._userOps !== userOpsAtStart) {
        biesDebug('[BIES Cart Drawer] refresh de fondo descartado: hay op de usuario en vuelo');
        return;
      }
      // 2c. Load de página sin cambios: el DOM del server ya es la verdad.
      if (skipSwapIfUnchanged && !stateChanged && !sectionHtml) {
        biesDebug('[BIES Cart Drawer] estado del load sin cambios — sin fetch ni swap');
        this._applyStableOrder();
        this._syncHeaderBubble(cart, null);
        this.updateRecommendedProducts(cart);
        document.dispatchEvent(new CustomEvent('bies:cart:updated', { detail: cart }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
        return;
      }

      // 3. Swap del HTML server. Si no hay HTML fresco (normalize cambió y gifts
      //    no, o la mutación no pidió sections), se re-pide una sola vez.
      if (!sectionHtml) {
        const rendered = await this._fetchCartAndSection();
        cart = rendered.cart;
        sectionHtml = rendered.html;
        bubbleHtml = rendered.bubbleHtml;
      }
      if (sectionHtml) {
        this.swapFromSections(sectionHtml);
      } else {
        biesDebug('[BIES Cart Drawer] sin section HTML — swap saltado (fallback)');
      }
      // Burbuja del header: siempre, con HTML de section o fallback por item_count.
      this._syncHeaderBubble(cart, bubbleHtml);
      // Recomendaciones: client-loaded, independientes del swap (antes se
      // disparaban desde renderCart, ahora bypassed) — escriben en su propio
      // contenedor que swapFromSections NO toca.
      this.updateRecommendedProducts(cart);
      document.dispatchEvent(new CustomEvent('bies:cart:updated', { detail: cart }));
      // Final state after bundle normalization and automatic-gift reconciliation.
      document.dispatchEvent(new CustomEvent('bies:cart:confirmed', { detail: { cart: cart, source: 'cart_drawer', drawerOpen: this.isOpen(), rewardTiers: this.REWARDS_TIERS } }));
      // Seam free-shipping: bies-free-shipping.js (barra de 20€) escucha
      // 'cart:refresh' (y 'cart:updated'/'product:added'). Emitimos 'cart:refresh'
      // —NO 'cart:updated'— porque el drawer escucha 'cart:updated' (loop) pero
      // NO 'cart:refresh': así la barra de envío gratis se actualiza sin reentrada.
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
    } catch (error) {
      console.error('[BIES Cart Drawer] Error processing cart:', error);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Auto-normalize bundle items in cart.
   * Detects two scenarios:
   *   1. Same variant added multiple times (qty > 1)
   *   2. Multiple tiers of the same bundle in cart (e.g. tier 1 + tier 2 = 3 units → tier 3)
   * Consolidates into the correct single tier with qty=1, giving the customer the best discount.
   */
  async normalizeBundleQuantities(cart) {
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const bundleVariants = window.BIES_BUNDLE_VARIANTS || {};
    const maxTier = window.BIES_MAX_TIER || 4;
    const tierDiscounts = window.BIES_TIER_DISCOUNTS || {};

    // Group cart items by bundleCode
    const bundleGroups = {};
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      const bundleInfo = bundleMap[String(item.variant_id)];
      if (bundleInfo) {
        const code = bundleInfo.bundleCode;
        if (!bundleGroups[code]) bundleGroups[code] = [];
        bundleGroups[code].push({
          tier: bundleInfo.tier,
          quantity: item.quantity,
          variantId: String(item.variant_id)
        });
      }
    }

    // Check each bundle group
    for (const code of Object.keys(bundleGroups)) {
      const group = bundleGroups[code];

      // Calculate total units across all items of this bundle
      let totalUnits = 0;
      for (const entry of group) {
        totalUnits += entry.tier * entry.quantity;
      }

      // Needs normalization if: multiple line items OR any item with qty > 1
      const needsNormalization = group.length > 1 || group[0].quantity > 1;
      if (!needsNormalization) continue;

      const targetTier = Math.min(totalUnits, maxTier);
      const tiers = bundleVariants[code];
      if (!tiers || !tiers[targetTier]) continue;

      biesDebug('[BIES Cart Drawer] Auto-normalizing:', code,
        'items=' + group.length, 'totalUnits=' + totalUnits, '→ tier=' + targetTier);

      // Remove all variants of this bundle at once using /cart/update.js
      const updates = {};
      for (const entry of group) {
        updates[entry.variantId] = 0;
      }
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });

      // Add the correct tier at qty=1
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: tiers[targetTier], quantity: 1 }] })
      });

      const discount = tierDiscounts[targetTier] || 0;
      if (discount > 0) {
        this.showNotification('¡Mejorado a pack ' + targetTier + 'x (-' + discount + '%)!');
      }

      return true; // Re-fetch cart before processing other bundles
    }

    return false;
  }


  /**
   * Subtotal de SOLO productos reales (excluye las líneas-regalo). Es la base
   * correcta para umbrales y barra de progreso. Cuando los regalos pasan a
   * precio real (descuento BXGY nativo, cutover), incluirlos en el total
   * inflaría el subtotal y dispararía acumulación runaway: un regalo cruzaría
   * el siguiente umbral → añadiría otro regalo → etc. Hoy los regalos van a 0€,
   * así que esto es behavior-preserving; tras el reprice, es lo que evita el bug.
   */
  nonGiftSubtotal(cart) {
    return (cart.items || [])
      .filter(i => !this.GIFT_VARIANT_IDS.has(i.variant_id))
      .reduce((sum, i) => sum + i.final_line_price, 0);
  }

  /**
   * Reconcilia los regalos escalonados (acumulativos) contra el subtotal
   * NO-regalo, en UNA sola llamada a /cart/update.js. Antes: N /cart/add.js
   * secuenciales + retries de 500ms (5-7 round-trips al cruzar umbral). El
   * descuento automático BXGY nativo (store-level) es quien deja el regalo
   * gratis en checkout; aquí solo garantizamos que la línea-regalo esté
   * presente o ausente según el umbral. /cart/update.js con qty 1 sobre una
   * variante ausente la AÑADE; con qty 0 la elimina — todo en un round-trip.
   */
  async syncGiftProducts(cart) {
    const base = this.nonGiftSubtotal(cart);
    const currentGifts = new Set(
      (cart.items || [])
        .filter(i => this.GIFT_VARIANT_IDS.has(i.variant_id))
        .map(i => i.variant_id)
    );

    const updates = {};
    let changed = false;

    // Regalos que corresponden por umbral (acumulativos: 40€ → +50€ → +70€).
    for (const tier of this.REWARDS_TIERS) {
      const should = base >= tier.threshold;
      const has = currentGifts.has(tier.giftVariantId);
      if (should && !has) { updates[String(tier.giftVariantId)] = 1; changed = true; }
      else if (!should && has) { updates[String(tier.giftVariantId)] = 0; changed = true; }
    }
    // Drenaje: cualquier variante-regalo presente que YA no es un tier vivo
    // (p.ej. Granola retirada) se elimina — evita la línea fantasma 0€.
    for (const vid of currentGifts) {
      if (!this.REWARDS_TIERS.some(t => t.giftVariantId === vid)) {
        updates[String(vid)] = 0; changed = true;
      }
    }

    if (!changed) {
      biesDebug('[BIES Gifts] No changes needed (base:', base, 'cents)');
      return { changed: false };
    }

    try {
      // sections= trae el HTML ya reconciliado en la MISMA respuesta → el
      // caller (processCart) lo swapea sin un round-trip extra.
      const resp = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, sections: BiesCartDrawer.SECTIONS })
      });
      if (!resp.ok) {
        console.error('[BIES Gifts] update.js failed:', resp.status, await resp.text());
        return { changed: false };
      }
      const data = await resp.json();
      data.__seq = ++this._seq;
      const { html, bubbleHtml } = this._sectionsFromResponse(data);
      biesDebug('[BIES Gifts] Reconciled gifts (base:', base, 'cents):', updates);
      return { changed: true, html, bubbleHtml, cart: data };
    } catch (err) {
      console.error('[BIES Gifts] Reconcile error:', err);
      return { changed: false };
    }
  }



  _itemKey(item) {
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const bi = bundleMap[String(item.variant_id)];
    return bi ? bi.bundleCode : String(item.variant_id);
  }

  _discountFromTitle(title) {
    const t = title || '';
    if (/^4x\s/i.test(t)) return 20;
    if (/^3x\s/i.test(t)) return 15;
    if (/^2x\s/i.test(t)) return 10;
    return 0;
  }




  async updateQuantity(lineIndex, action) {
    const item = document.querySelector(`[data-line-index="${lineIndex}"]`);
    if (!item) return;

    // Check if this is a bundle product
    const variantId = item.getAttribute('data-variant-id');
    const bundleMap = window.BIES_VARIANT_TO_BUNDLE || {};
    const bundleInfo = variantId ? bundleMap[String(variantId)] : null;

    if (bundleInfo) {
      // Bundle product → swap tiers instead of changing quantity
      await this.swapBundleTier(lineIndex, variantId, bundleInfo, action);
      return;
    }

    // Non-bundle product → normal quantity change. Sin UI optimista: el server
    // es el único renderer (opción D); el swap pinta el estado autoritativo.
    const qtyElement = item.querySelector('.bies-cart-item__qty-value');
    if (!qtyElement) return;

    const currentQty = parseInt(qtyElement.textContent);
    const newQty = action === 'plus' ? currentQty + 1 : currentQty - 1;

    if (newQty < 0) return;
    if (newQty === 0) { this.removeItem(lineIndex); return; }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          quantity: newQty,
          sections: BiesCartDrawer.SECTIONS
        })
      });
      if (!response.ok) throw new Error('Failed to update quantity');

      const data = await response.json();
      const { html, bubbleHtml } = this._sectionsFromResponse(data);
      await this.processCart(data, { skipNormalization: true, html, bubbleHtml });

      if (window.dataLayer) {
        window.dataLayer.push({ event: 'cart_quantity_updated', quantity: newQty, line_index: lineIndex });
      }
    } catch (error) {
      console.error('[BIES Cart Drawer] Error updating quantity:', error);
      this.showNotification('Error al actualizar cantidad', 'error');
      // Re-sincroniza desde el server (sin estado optimista que revertir).
      try { await this.updateCart(); } catch (_) {}
    }
  }

  /**
   * Swap between bundle tiers (remove current tier, add new tier)
   */
  async swapBundleTier(lineIndex, currentVariantId, bundleInfo, action) {
    const maxTier = window.BIES_MAX_TIER || 4;
    const minTier = window.BIES_MIN_TIER || 1;
    const bundleVariants = window.BIES_BUNDLE_VARIANTS || {};
    const tierDiscounts = window.BIES_TIER_DISCOUNTS || {};

    const currentTier = bundleInfo.tier;
    const bundleCode = bundleInfo.bundleCode;
    const newTier = action === 'plus' ? currentTier + 1 : currentTier - 1;

    biesDebug('[BIES Cart Drawer] Bundle swap:', bundleCode, 'tier', currentTier, '→', newTier);

    // At max tier and pressing +, do nothing
    if (newTier > maxTier) {
      biesDebug('[BIES Cart Drawer] Already at max tier');
      return;
    }

    // Below min tier, remove item from cart
    if (newTier < minTier) {
      biesDebug('[BIES Cart Drawer] Below min tier, removing item');
      this.removeItem(lineIndex);
      return;
    }

    // Get new variant ID
    const tiers = bundleVariants[bundleCode];
    if (!tiers || !tiers[newTier]) {
      console.error('[BIES Cart Drawer] No variant found for', bundleCode, 'tier', newTier);
      this.showNotification('Error: tier no disponible', 'error');
      return;
    }

    const newVariantId = tiers[newTier];

    try {
      this.setLoading(true);

      // Step 1: Remove current item (set quantity to 0)
      const removeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: parseInt(lineIndex, 10),
          quantity: 0
        })
      });

      if (!removeResponse.ok) {
        throw new Error('Failed to remove current tier');
      }

      // Step 2: Add new tier variant (quantity 1) + sections= para traer el
      // HTML renderizado en la misma respuesta (evita el /cart.js extra).
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: newVariantId, quantity: 1 }],
          sections: BiesCartDrawer.SECTIONS
        })
      });

      if (!addResponse.ok) {
        // Rollback: re-add the original variant
        console.error('[BIES Cart Drawer] Add failed, rolling back...');
        await this.rollbackBundleSwap(currentVariantId);
        this.showNotification('Error al cambiar pack, restaurado original', 'error');
        await this.updateCart();
        return;
      }

      // Step 3: carrito + HTML de la respuesta de add.js (sections=)
      const addData = await addResponse.json();
      const cart = this._cartFromSectionsResponse(addData) || await this._authoritativeCart();
      const { html, bubbleHtml } = this._sectionsFromResponse(addData);
      await this.processCart(cart, { html, bubbleHtml });

      // Notification
      const discount = tierDiscounts[newTier] || 0;
      if (discount > 0) {
        this.showNotification(`Cambiado a pack ${newTier}x (-${discount}%)`);
      } else {
        this.showNotification('Cambiado a 1 unidad');
      }

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'bundle_tier_swapped',
          bundle_code: bundleCode,
          from_tier: currentTier,
          to_tier: newTier,
          discount_percent: discount
        });
      }

      // Trigger free shipping widget update (cart already fetched above)
      if (window.updateFreeShippingBar) {
        window.updateFreeShippingBar(cart.total_price);
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error swapping bundle tier:', error);
      this.showNotification('Error al cambiar cantidad', 'error');
      await this.updateCart();
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Rollback: re-add the original variant if add of new tier fails
   */
  async rollbackBundleSwap(originalVariantId) {
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: originalVariantId,
            quantity: 1
          }]
        })
      });
      biesDebug('[BIES Cart Drawer] Rollback successful, re-added variant:', originalVariantId);
    } catch (rollbackError) {
      console.error('[BIES Cart Drawer] Rollback FAILED:', rollbackError);
    }
  }

  async removeItem(lineIndex, variantId = null) {
    // Re-resolver el índice actual por variant-id (el swap pudo re-numerar la
    // lista entre el click y esta ejecución encolada). Si el item ya no está
    // (p.ej. doble-click que ya lo borró), SKIP — nunca borrar por un índice
    // stale que ahora apunta al item de al lado. Fix M1 (mega-code-review).
    if (variantId) {
      const el = this.drawer && this.drawer.querySelector(
        `#bies-cart-items .bies-cart-item[data-variant-id="${variantId}"]`);
      if (!el) {
        biesDebug('[BIES Cart Drawer] removeItem: variant', variantId, 'ya no está en la lista — skip');
        return;
      }
      lineIndex = el.getAttribute('data-line-index');
    }
    biesDebug('[BIES Cart Drawer] Removing item from line:', lineIndex);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          quantity: 0,
          sections: BiesCartDrawer.SECTIONS
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      const data = await response.json();
      const { html, bubbleHtml } = this._sectionsFromResponse(data);
      await this.processCart(data, { skipNormalization: true, html, bubbleHtml });

      this.showNotification('Producto eliminado del carrito');

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'cart_item_removed',
          line_index: lineIndex
        });
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error removing item:', error);
      this.showNotification('Error al eliminar producto', 'error');
    }
  }

  async addRecommendedProduct(variantId, button) {
    if (!variantId) return;

    const originalText = button.innerHTML;
    button.disabled = true;
    button.innerHTML = 'Añadiendo...';

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }],
          sections: BiesCartDrawer.SECTIONS
        })
      });

      if (!addResponse.ok) throw new Error('Failed to add product');

      // (14-jul) JSON embebido de la section = carrito completo en la misma
      // respuesta; el HTML de la section se pasa a processCart para el swap
      // sin round-trip extra.
      const addData = await addResponse.json();
      const cart = await this._cartFromAddResponse(addData);
      const { html, bubbleHtml } = this._sectionsFromResponse(addData);
      await this.processCart(cart, { html, bubbleHtml });

      this.showNotification('¡Producto añadido!');

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'cart_recommended_added',
          variant_id: variantId
        });
      }

    } catch (error) {
      console.error('[BIES Cart Drawer] Error adding recommended product:', error);
      this.showNotification('Error al añadir producto', 'error');
      button.innerHTML = originalText;
      button.disabled = false;
    }
  }


  /**
   * BIES Smart Recommendations System
   * Uses Shopify Recommendations API + intelligent filtering
   */
  async updateRecommendedProducts(cart) {
    const recommendedContainer = document.getElementById('bies-cart-recommended-items');
    if (!recommendedContainer) {
      biesDebug('[BIES Smart Recs] Recommended container not found');
      return;
    }

    const recommendedSection = recommendedContainer.closest('.bies-cart-drawer__recommended');

    // BIES (17-jun): evitar el "flash" de recomendaciones al cambiar SOLO la cantidad.
    // BIES (14-jul, seamless): NUNCA destruir el contenido visible con un
    // "Cargando..." — las recomendaciones actuales permanecen mientras las
    // nuevas se calculan en background (con caches), y solo hay UN swap al
    // final si el resultado difiere de lo pintado. Esto elimina el bloque en
    // blanco + salto de layout que congelaba el cajón al añadir/quitar items.
    const recoKey = (cart.items || []).map(i => i.product_id).sort().join(',');
    if (recoKey === this._lastRecoKey) return;
    this._lastRecoKey = recoKey;

    // Carrera: si otra mutación relanza recos mientras esta vuela, gana la
    // más nueva — la vieja no pinta.
    const runId = ++this._recoRunId;

    // Get cart product IDs and types
    const cartProductIds = cart.items.map(item => item.product_id);
    // let (no const) para que el catch pueda referenciarla aunque el
    // Promise.all de abajo falle antes de asignarla.
    let cartProductTypes = [];

    try {
      // (14-jul) Tipos y recos por item son independientes hasta el filtrado
      // → las dos olas corren en PARALELO. Con caches fríos (primera carga)
      // esto reduce la latencia del primer cómputo aproximadamente a la mitad.
      const [types, recsArrays] = await Promise.all([
        this.getCartProductTypes(cart.items),
        Promise.all(cart.items.map(item => this.fetchShopifyRecommendations(item.product_id)))
      ]);
      cartProductTypes = types;
      const allRecommendations = recsArrays.flat();

      biesDebug('[BIES Smart Recs] Cart product IDs:', cartProductIds);
      biesDebug('[BIES Smart Recs] Cart product types:', cartProductTypes);
      biesDebug('[BIES Smart Recs] Raw recommendations from API:', allRecommendations.length);

      // Step 2: Deduplicate and filter recommendations
      const seen = new Set();
      const filteredRecommendations = allRecommendations.filter(product => {
        // Skip if already seen
        if (seen.has(product.id)) return false;
        seen.add(product.id);

        // Skip if already in cart
        if (cartProductIds.includes(product.id)) {
          biesDebug('[BIES Smart Recs] Excluding (in cart):', product.title);
          return false;
        }

        // Skip if not available
        if (!product.available) return false;

        // Skip hidden products (tags can be array or string)
        if (product.tags) {
          const tagsLower = Array.isArray(product.tags)
            ? product.tags.map(t => t.toLowerCase()).join(' ')
            : product.tags.toLowerCase();
          if (tagsLower.includes('hidden')) return false;
        }

        // Step 3: SMART FILTER - Exclude same product type
        const productType = this.inferProductType(product);
        if (cartProductTypes.includes(productType)) {
          biesDebug('[BIES Smart Recs] Excluding (same type "' + productType + '"):', product.title);
          return false;
        }

        // Skip packs if user has individual products of that type
        if (this.isPackContainingCartTypes(product, cartProductTypes)) {
          biesDebug('[BIES Smart Recs] Excluding (pack contains cart type):', product.title);
          return false;
        }

        return true;
      });

      biesDebug('[BIES Smart Recs] Filtered recommendations:', filteredRecommendations.length);

      // Step 4: If not enough recommendations, get fallback products
      let finalRecommendations = filteredRecommendations.slice(0, 3);

      if (finalRecommendations.length < 3) {
        biesDebug('[BIES Smart Recs] Not enough recs, fetching fallback...');
        const fallbackProducts = await this.getFallbackRecommendations(
          cartProductIds,
          cartProductTypes,
          finalRecommendations.map(p => p.id)
        );
        finalRecommendations = finalRecommendations.concat(fallbackProducts).slice(0, 3);
      }

      biesDebug('[BIES Smart Recs] Final recommendations:', finalRecommendations.map(p => p.title));

      // Llegó tarde: hay un run más nuevo en vuelo — no pintar estado viejo.
      if (runId !== this._recoRunId) return;

      // Step 5: Render recommendations
      if (finalRecommendations.length === 0) {
        if (recommendedSection) recommendedSection.style.display = 'none';
        this._lastRenderedRecoIds = '';
        return;
      }

      if (recommendedSection) recommendedSection.style.display = 'block';
      // Swap ÚNICO y solo si el resultado difiere de lo pintado — mismo set
      // de recos = cero toques al DOM (cero recarga de <img>, cero reflow).
      const renderedIds = finalRecommendations.map(p => p.id).join(',');
      if (renderedIds !== this._lastRenderedRecoIds) {
        recommendedContainer.innerHTML = finalRecommendations.map(product => this.renderRecommendedProduct(product)).join('');
        this._lastRenderedRecoIds = renderedIds;
      }

      // Analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'smart_recommendations_shown',
          products: finalRecommendations.map(p => p.title),
          cart_types: cartProductTypes
        });
      }

    } catch (error) {
      console.error('[BIES Smart Recs] Error:', error);
      if (runId !== this._recoRunId) return;
      // Permitir reintento en la próxima mutación (el guard recoKey ya quedó
      // seteado y sin esto bloquearía recargas del mismo set tras un fallo).
      this._lastRecoKey = null;
      this._lastRenderedRecoIds = '';
      // Fallback to simple recommendations
      await this.showFallbackRecommendations(cartProductIds, cartProductTypes, recommendedContainer, recommendedSection);
    }
  }

  /**
   * Get product types for all items in cart
   */
  async getCartProductTypes(items) {
    const results = await Promise.all(items.map(async (item) => {
      // Cache por handle (14-jul): el tipo de un producto no cambia durante
      // la vida de la página — sin esto cada mutación re-fetcheaba TODOS los
      // /products/{handle}.js del carrito.
      if (this._productTypeCache.has(item.handle)) {
        return this._productTypeCache.get(item.handle);
      }
      try {
        const response = await fetch(`/products/${item.handle}.js`);
        const product = await response.json();
        const type = this.inferProductType(product);
        this._productTypeCache.set(item.handle, type);
        return type;
      } catch (e) {
        // Fallback por título — NO se cachea (un fetch transitorio fallido
        // no debe fosilizar el tipo inferido de segunda mano).
        return this.inferTypeFromTitle(item.product_title);
      }
    }));
    return [...new Set(results.filter(Boolean))];
  }

  /**
   * Infer product type from product data or title
   */
  inferProductType(product) {
    // First, check if product_type is defined
    if (product.product_type && product.product_type.trim() !== '') {
      return product.product_type.toLowerCase();
    }

    // Otherwise, infer from title
    return this.inferTypeFromTitle(product.title);
  }

  /**
   * Infer product type from title using keywords
   */
  inferTypeFromTitle(title) {
    const titleLower = (title || '').toLowerCase();

    // Order matters - more specific matches first
    if (titleLower.includes('batido de proteína') || titleLower.includes('batido proteína') || titleLower.includes('proteina')) {
      return 'proteina';
    }
    if (titleLower.includes('pancakes') || titleLower.includes('tortitas')) {
      return 'pancakes';
    }
    if (titleLower.includes('granola')) {
      return 'granola';
    }
    if (titleLower.includes('café') || titleLower.includes('cafe')) {
      return 'cafe';
    }
    if (titleLower.includes('matcha')) {
      return 'matcha';
    }
    if (titleLower.includes('bebida instantánea') || titleLower.includes('bebida instantanea')) {
      return 'bebida';
    }
    if (titleLower.includes('shaker')) {
      return 'shaker';
    }
    if (titleLower.includes('endulzante') || titleLower.includes('stevia')) {
      return 'endulzante';
    }
    if (titleLower.includes('boost') || titleLower.includes('pack') || titleLower.includes('rutina') || titleLower.includes('desayuno')) {
      return 'pack';
    }

    return 'otro';
  }

  /**
   * Check if a pack contains products of types already in cart
   */
  isPackContainingCartTypes(product, cartTypes) {
    const titleLower = (product.title || '').toLowerCase();

    // Check if it's a pack
    if (!titleLower.includes('boost') && !titleLower.includes('pack') &&
        !titleLower.includes('rutina') && !titleLower.includes('desayuno')) {
      return false;
    }

    // Check if pack contains cart types
    for (const type of cartTypes) {
      if (type === 'proteina' && (titleLower.includes('proteína') || titleLower.includes('proteina'))) {
        return true;
      }
      if (type === 'cafe' && titleLower.includes('café')) {
        return true;
      }
      if (type === 'granola' && titleLower.includes('granola')) {
        return true;
      }
      if (type === 'shaker' && titleLower.includes('shaker')) {
        return true;
      }
      if (type === 'bebida' && titleLower.includes('bebida')) {
        return true;
      }
    }

    return false;
  }

  /**
   * Fetch recommendations from Shopify API
   */
  async fetchShopifyRecommendations(productId) {
    // Cache por product_id (14-jul): mismas recos durante la sesión de página.
    if (this._recsCache.has(productId)) return this._recsCache.get(productId);
    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=related`);
      const data = await response.json();
      const recs = data.products || [];
      this._recsCache.set(productId, recs);
      return recs;
    } catch (error) {
      biesDebug('[BIES Smart Recs] API error for product', productId, error);
      return [];
    }
  }

  /**
   * Get fallback recommendations when API doesn't provide enough
   */
  async getFallbackRecommendations(excludeIds, excludeTypes, alreadyRecommendedIds) {
    try {
      // Cache del pool de fallback (14-jul): /products.json es idéntico
      // durante la sesión de página — un solo fetch.
      if (!this._allProductsCache) {
        const response = await fetch('/products.json?limit=30');
        const data = await response.json();
        this._allProductsCache = data.products || [];
      }

      const allExcludeIds = [...excludeIds, ...alreadyRecommendedIds];

      return this._allProductsCache.filter(product => {
        if (allExcludeIds.includes(product.id)) return false;
        if (!product.available) return false;
        // /products.json devuelve tags como ARRAY (el main path ya maneja
        // ambos — sin este guard, .toLowerCase() sobre array lanza TypeError
        // dentro del filter y el catch dejaba el fallback SIEMPRE vacío).
        if (product.tags) {
          const tagsLower = Array.isArray(product.tags)
            ? product.tags.map(t => t.toLowerCase()).join(' ')
            : product.tags.toLowerCase();
          if (tagsLower.includes('hidden')) return false;
        }

        const type = this.inferProductType(product);
        if (excludeTypes.includes(type)) return false;

        // Also exclude packs
        if (type === 'pack') return false;

        return true;
      }).slice(0, 3);
    } catch (error) {
      biesDebug('[BIES Smart Recs] Fallback error:', error);
      return [];
    }
  }

  /**
   * Show simple fallback recommendations on error
   */
  async showFallbackRecommendations(excludeIds, excludeTypes, container, section) {
    try {
      const products = await this.getFallbackRecommendations(excludeIds, excludeTypes, []);

      if (products.length === 0) {
        if (section) section.style.display = 'none';
        return;
      }

      if (section) section.style.display = 'block';
      container.innerHTML = products.slice(0, 3).map(product => this.renderRecommendedProduct(product)).join('');
    } catch (error) {
      console.error('[BIES Smart Recs] Fallback display error:', error);
      if (section) section.style.display = 'none';
    }
  }

  /**
   * Render a single recommended product
   */
  renderRecommendedProduct(product) {
    const variant = product.variants ? product.variants[0] : product;
    const image = product.images && product.images.length > 0
      ? product.images[0]
      : (product.featured_image || '');
    const price = variant.price || product.price;
    const variantId = variant.id || product.id;

    return `
      <div class="bies-cart-recommended-item">
        ${image ? `
          <img
            src="${typeof image === 'string' ? image : image.src}"
            alt="${this.escapeHtml(product.title)}"
            class="bies-cart-recommended-item__image"
            loading="lazy"
          >
        ` : ''}
        <div class="bies-cart-recommended-item__details">
          <h4 class="bies-cart-recommended-item__title">${this.escapeHtml(product.title)}</h4>
          <p class="bies-cart-recommended-item__price">${this.formatMoney(price)}</p>
        </div>
        <button
          class="bies-cart-recommended-item__add-btn"
          data-add-recommended
          data-variant-id="${variantId}"
        >
          Añadir
        </button>
      </div>
    `;
  }

  // Skeleton del primer add (14-jul): cuando el carrito está vacío y el user
  // añade su primer producto, la apertura optimista NO debe enseñar "tu
  // carrito está vacío" mientras vuela la red. Vive en la clase (no en el
  // interceptor) porque manipula los mismos nodos que renderCart posee —
  // un solo dueño de esos IDs. Devuelve true si lo mostró.
  _showFirstAddSkeleton() {
    const emptyEl = document.getElementById('bies-cart-empty');
    const bodyEl = document.getElementById('bies-cart-body-content');
    const itemsEl = document.getElementById('bies-cart-items');
    if (!emptyEl || !bodyEl || !itemsEl || emptyEl.style.display === 'none') return false;
    emptyEl.style.display = 'none';
    bodyEl.style.display = 'flex';
    itemsEl.innerHTML = '<div class="bies-cart-item bies-cart-item--skeleton" aria-hidden="true"></div>';
    return true;
  }

  // Deshace el skeleton restaurando el empty state — SOLO para el caso de
  // doble fallo de red (add falló Y el refetch de recovery también); en
  // cualquier flujo normal renderCart pisa el skeleton con datos reales.
  _hideFirstAddSkeleton() {
    const emptyEl = document.getElementById('bies-cart-empty');
    const bodyEl = document.getElementById('bies-cart-body-content');
    const itemsEl = document.getElementById('bies-cart-items');
    if (itemsEl) itemsEl.innerHTML = '';
    if (bodyEl) bodyEl.style.display = 'none';
    if (emptyEl) emptyEl.style.display = 'flex';
  }

  // ── API pública de "añadir" (8-sep): un solo dueño del flujo optimista ──
  // La usan el interceptor de formularios (abajo) y flujos externos que hacen
  // su propio fetch a /cart/add.js (tu-propio-pack.js). Contrato:
  //   const token = beginOptimisticAdd();        // ANTES de la red: abre + spinner
  //   ...fetch('/cart/add.js', { ...sections: BiesCartDrawer.SECTIONS })
  //   await commitAddResponse(addData, token);   // ok  → reconcilia y pinta
  //   await recoverAfterFailedAdd(token);        // ko  → restaura vista real
  beginOptimisticAdd() {
    this._userOps++;                 // las ops de fondo en vuelo NO deben pisar esta
    this.open({ skipUpdate: true });
    this.setLoading(true);           // spinner del drawer visible DURANTE la red
    const skeletonShown = this._showFirstAddSkeleton();
    return { skeletonShown, released: false };
  }

  _releaseAddToken(token) {
    if (!token || token.released) return;
    token.released = true;
    this.setLoading(false);
  }

  // Cuerpo del commit, SIN cola: para llamarlo desde dentro de una op serie.
  async _applyAddResponse(addData) {
    const cart = await this._cartFromAddResponse(addData);
    const { html, bubbleHtml } = this._sectionsFromResponse(addData);
    await this.processCart(cart, { html, bubbleHtml });
    return cart;
  }

  commitAddResponse(addData, token = null) {
    return this._serial(async () => {
      try { await this._applyAddResponse(addData); }
      finally { this._releaseAddToken(token); }
    });
  }

  // Restaura la VISTA real tras un add fallido (p.ej. sin stock). Swap del
  // HTML server SIN processCart: tras un fallo no relanzamos mutaciones
  // (normalize/gift-sync); la próxima op exitosa ya reconcilia. SIN cola.
  async _recoverViewAfterFailedAdd(token = null) {
    try {
      const rendered = await this._fetchCartAndSection();
      if (rendered.html) this.swapFromSections(rendered.html);
      this._syncHeaderBubble(rendered.cart, rendered.bubbleHtml);
    } catch (_) {
      // Doble fallo de red: al menos deshacer el skeleton para no dejar
      // un shimmer eterno sobre un carrito que sigue vacío.
      if (token && token.skeletonShown) this._hideFirstAddSkeleton();
    } finally {
      this._releaseAddToken(token);
    }
  }

  recoverAfterFailedAdd(token = null) {
    return this._serial(() => this._recoverViewAfterFailedAdd(token));
  }

  // Flujo completo de "añadir desde formulario": lo usan el interceptor de
  // submit y el drenaje del shim inline. Idempotente por formulario mientras
  // el add está en vuelo (doble tap en móvil = un solo add).
  // Beacon de métrica de campo vía bies-rum.js (no-op si no está cargado).
  static rum(name, value, rating) {
    try { if (window.biesRum) window.biesRum.send(name, value, rating); } catch (_) {}
  }

  // spec bies-theme-rendimiento-cookies-y-senal-v1 task 04 (reassigned):
  // helpers for the `bies:cart:added` contract. Same shape as the inline
  // shim in layout/theme.liquid. Only user-initiated adds confirmed by a 2xx
  // from /cart/add.js call these; background adds (normalizeBundleQuantities,
  // swapBundleTier, rollbackBundleSwap, addRecommendedProduct) never do.
  static newAddRequestId() {
    return 'atc_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
  }

  static emitCartAdded(token, addData, source) {
    if (!addData || typeof addData !== 'object') return;
    // /cart/add.js returns { items: [...] } for multi-item payloads and the
    // bare line item for a single `id`/`quantity` form post. Normalize to an
    // array and keep Shopify's own field names (variant_id, quantity,
    // final_price, price, product_id, title) untouched.
    const items = Array.isArray(addData.items) ? addData.items : [addData];
    try {
      document.dispatchEvent(new CustomEvent('bies:cart:added', {
        detail: { token: token, items: items, source: source }
      }));
    } catch (error) {
      console.error('[BIES Cart Drawer] bies:cart:added dispatch failed:', error);
    }
  }

  submitAddForm(form, formData, { responsePromise = null, tapAt = null } = {}) {
    if (form && form.dataset.biesAdding === '1') {
      biesDebug('[BIES Cart Drawer] add ya en vuelo para este form — ignorado');
      return this._opQueue;
    }
    BiesCartDrawer.setFormSubmitting(form, true);
    const token = this.beginOptimisticAdd();
    const formId = (form && form.id) || 'unknown';
    const t0 = (typeof tapAt === 'number') ? tapAt : performance.now();

    // Encolar en la cola serie (misma que +/-/remove) para que un "añadir"
    // no se pise con otra operación de carrito en vuelo.
    return this._serial(async () => {
      try {
        let addData;
        if (responsePromise) {
          // El shim inline ya hizo el POST en el tap (con sections=).
          addData = await responsePromise;
        } else {
          // add.js con sections= devuelve en la MISMA respuesta el drawer y la
          // burbuja del header renderizados → sin segundo fetch a /cart.js.
          formData.append('sections', BiesCartDrawer.SECTIONS.join(','));
          // spec bies-theme-rendimiento-cookies-y-senal-v1 task 04 (reassigned):
          // one id per HTTP request, generated BEFORE the fetch, so the
          // AddToCart listener can dedupe by request instead of by time.
          const addRequestId = BiesCartDrawer.newAddRequestId();
          const response = await fetch('/cart/add.js', { method: 'POST', body: formData });
          if (!response.ok) throw new Error('Failed to add to cart: HTTP ' + response.status);
          addData = await response.json();
          // Emit ONLY here: a confirmed 2xx add initiated by the user. The
          // theme shim emits its own event (source 'theme_shim') for the
          // responsePromise branch, so we never fire twice for one request.
          BiesCartDrawer.emitCartAdded(addRequestId, addData, 'cart_drawer');
        }
        await this._applyAddResponse(addData);
        this._releaseAddToken(token);
        // Métrica de campo: tap → producto pintado (incluye la espera pre-JS si la hubo).
        const render = Math.round(performance.now() - t0);
        BiesCartDrawer.rum('ATC_RENDER', render, render < 1000 ? 'good' : render < 2500 ? 'needs-improvement' : 'poor');
        if (window.dataLayer) window.dataLayer.push({ event: 'add_to_cart', form_id: formId });
      } catch (error) {
        console.error('[BIES Cart Drawer] Error adding to cart:', error);
        alert('Error al añadir el producto al carrito');
        await this._recoverViewAfterFailedAdd(token);
      } finally {
        BiesCartDrawer.setFormSubmitting(form, false);
      }
    });
  }

  // Feedback en el botón emisor (fix 8-sep): el interceptor de submit corta
  // product-form.js de Dawn con stopImmediatePropagation, así que nadie ponía
  // `.loading` ni el spinner → en móvil el botón parecía muerto y admitía
  // pulsaciones múltiples. Replicamos exactamente lo que hacía Dawn.
  static setFormSubmitting(form, on) {
    if (!form) return;
    form.dataset.biesAdding = on ? '1' : '';
    const btn = form.querySelector('[type="submit"]');
    if (!btn) return;
    btn.classList.toggle('loading', on);
    if (on) btn.setAttribute('aria-disabled', 'true'); else btn.removeAttribute('aria-disabled');
    const spinner = btn.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
  }

  setLoading(isLoading) {
    if (!this.drawer) return;
    // Refcount (13-jul): varias cadenas (processCart anidado en swapBundleTier)
    // pueden pedir loading a la vez; el toggle suelto hacía que el finally de
    // una cadena limpiara el estado que otra aún necesitaba.
    this._loadingCount = Math.max(0, (this._loadingCount || 0) + (isLoading ? 1 : -1));
    this.drawer.classList.toggle('is-loading', this._loadingCount > 0);
  }

  showNotification(message, type = 'success') {
    // BIES (17-jun): el cliente pidió eliminar la "caja verde" de avisos del carrito
    // ("Cambiado a pack...", "Añadido", etc.). La desactivamos en el origen para que
    // NO aparezca nunca (independiente de caché/CSS). No afecta a la lógica de
    // packs/descuentos: esto solo pintaba el toast visual.
    return;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }


  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }
}

// Initialize cart drawer
const biesCartDrawer = new BiesCartDrawer();

// Expose globally for external triggers
window.biesCartDrawer = biesCartDrawer;

// ── Relevo del shim inline de theme.liquid (fix 8-sep) ─────────────────────
// Este script va con `defer` y en móvil ejecuta a los 7-20s (HTML de ~580KB);
// el botón "Añadir" es tocable desde los ~3s. Un tap en esa ventana hacía
// submit NATIVO → navegación completa a /cart ("clic muerto" / "carrito
// desconectado", reproducido en producción con iPhone emulado + 3G/4G).
// El shim `window.__biesEarlyAdd` (inline en <head>) captura esos submits y
// los guarda; aquí paramos el shim ANTES de registrar nuestro listener (su
// stopImmediatePropagation nos bloquearía) y los drenamos cuando el drawer
// esté montado.
const biesEarlyAdds = (window.__biesEarlyAdd && typeof window.__biesEarlyAdd.stop === 'function')
  ? window.__biesEarlyAdd.stop()
  : [];
biesCartDrawer._drainEarlyAdds = function () {
  if (!biesEarlyAdds.length) return;
  biesDebug('[BIES Cart Drawer] Draining', biesEarlyAdds.length, 'early add(s) captured before JS');
  // El shim ya disparó el POST /cart/add.js en el tap: aquí solo pintamos su
  // respuesta (o su error), sin repetir el add. Sus clases is-open/is-loading
  // las normaliza beginOptimisticAdd() + el refcount de setLoading.
  biesEarlyAdds.splice(0).forEach(p => {
    this.drawer && this.drawer.classList.remove('is-loading');  // la puso el shim sin refcount
    // Métrica de campo: cuánto esperó el usuario entre el tap y que este JS existiera.
    const gap = Math.round(performance.now() - p.at);
    BiesCartDrawer.rum('ATC_PREJS', gap, gap < 2000 ? 'good' : gap < 5000 ? 'needs-improvement' : 'poor');
    this.submitAddForm(p.form, p.formData, { responsePromise: p.response, tapAt: p.at });
  });
};
if (biesCartDrawer.drawer) biesCartDrawer._drainEarlyAdds();
else document.addEventListener('DOMContentLoaded', () => biesCartDrawer._drainEarlyAdds());

// Listen for add-to-cart events from product forms
document.addEventListener('submit', (e) => {
  const form = e.target;

  // Check if it's a product form - be more aggressive in detection
  const isProductForm = form.getAttribute('action') === '/cart/add' ||
                        form.getAttribute('action')?.includes('/cart/add') ||
                        form.classList.contains('product-form') ||
                        form.querySelector('[name="id"]'); // Has variant ID input

  if (isProductForm) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    biesDebug('[BIES Cart Drawer] Intercepted add-to-cart form submission');
    biesCartDrawer.submitAddForm(form, new FormData(form));
    return false;
  }
}, true); // Use capture phase to intercept earlier

// Intercept cart icon clicks in header
document.addEventListener('click', (e) => {
  const cartIcon = e.target.closest('#cart-icon-bubble');
  if (cartIcon) {
    e.preventDefault();
    e.stopPropagation();
    biesDebug('[BIES Cart Drawer] Cart icon clicked, opening drawer');
    biesCartDrawer.open();
  }
}, true);

// Also intercept any link to /cart to open drawer instead
document.addEventListener('click', (e) => {
  const link = e.target.closest('a[href="/cart"]');
  if (link) {
    e.preventDefault();
    e.stopPropagation();
    biesDebug('[BIES Cart Drawer] Cart link clicked, opening drawer');
    biesCartDrawer.open();
  }
}, true);

biesDebug('[BIES Cart Drawer] Initialized successfully');
